import { ModalContextProvider, StandardModal } from '@pbk-web/ui';
import type { MouseEvent as ReactMouseEvent } from 'react';
import { useCallback, useMemo, useRef, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import MiddleTruncate from '../components/shared/MiddleTruncate';

export const useWarnOutboundLink = () => {
  const [blockedUrl, setBlockedUrl] = useState('');
  const aTag = useRef<HTMLAnchorElement>(undefined);
  const { t } = useTranslation('common');

  const shouldBlock = useCallback((href: string) => {
    if (!href) {
      return false;
    }

    const target = new URL(href);
    if (target.protocol === 'blob:') return false;

    const currentProtocol = window.location.protocol;
    const currentHost = window.location.host;

    // Block if protocol or host is different
    return currentProtocol !== target.protocol || currentHost !== target.host;
  }, []);

  const onClick = useCallback(
    (e: ReactMouseEvent<HTMLAnchorElement> | MouseEvent) => {
      const anchor = (e.target as Element).closest('a');

      if (!anchor) {
        return;
      }

      // allow through if user confirmed it -> aTag will be set if we're showing the modal
      // doing this so that document.createElement a tags still work
      if (aTag.current) {
        setBlockedUrl('');
        aTag.current = undefined;
      } else if (shouldBlock(anchor.href)) {
        e.preventDefault();
        e.stopPropagation();
        setBlockedUrl(anchor.href);
        aTag.current = anchor;
      }
    },
    [shouldBlock],
  );

  const modalCancel = useCallback(() => {
    setBlockedUrl('');
    aTag.current = undefined;
  }, []);

  const modal = useMemo(() => {
    return (
      <ModalContextProvider value={{ open: !!blockedUrl, onClose: modalCancel, modalWidth: 'w-2/5' }}>
        <StandardModal
          title={t('external-link.title')}
          cancelButtonTitle={t('external-link.cancel')}
          confirmButtonTitle={t('external-link.confirm')}
          onCancelClick={modalCancel}
          onConfirmClick={() => aTag.current?.click()}
        >
          <Trans t={t} i18nKey="external-link.body-1" components={{ p: <p className="my-2" /> }} />
          <div className="text-hyperlink-1 font-bold">
            <MiddleTruncate>{blockedUrl}</MiddleTruncate>
          </div>
          <Trans t={t} i18nKey="external-link.body-2" components={{ p: <p className="my-2" /> }} />
        </StandardModal>
      </ModalContextProvider>
    );
  }, [blockedUrl, modalCancel, t]);

  return { onClick, modal };
};
